import React, { useState, useRef, useEffect, useMemo } from "react";
import AuditReportList from "./AuditReportList";
import {
  Container,
  Grid,
  Tooltip,
} from "@mui/material";
import FilterSelect from "../../../components/FilterSelect";
import RoundButton from "../../../components/RoundButton";
import Fab from "../../../components/Fab";
import Snackbar from "../../../components/Snackbar";
import { getViewPickList } from "./Service";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { commonStrings, auditReportStrings } from "../../../components/StringBundle";
import dayjs from "dayjs";
import { useGlobalStyles } from "../../../components/globalStyles";
import { company_name_list, isCompanyRestrictedRole, getCompanyFilterList, getDefaultCompany} from "../../common";
import { roles } from "../../../constants/roles";
import useToken from "../../../hooks/useToken";
import { hideExportExcel } from "../../../utils/utility";

const AuditReport = () => {
  const globalClasses = useGlobalStyles();
  const { getCurrentUserDetails } = useToken();
  const currentUserDetails = getCurrentUserDetails();
  const currentUserRole = currentUserDetails?.role;
  const exportExcelRef = useRef();
  const dateformat = "DD-MM-YYYY";
  const auditReportString = auditReportStrings.forms;
  const savedPageNumber = sessionStorage.getItem("auditReport.selectedpagenumber");
  const savedFromDate = sessionStorage.getItem("auditReport.fromDate");
  const savedToDate = sessionStorage.getItem("auditReport.toDate");
  const savedStatus = sessionStorage.getItem("Auditreportfilter");
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showSnack, setSnack] = useState({});
  const [activepage, setActivepage] = useState(savedPageNumber ? parseInt(savedPageNumber) : 1);
  const [status, setStatus] = useState(savedStatus || "");
  const [company, setCompany] = useState(isCompanyRestrictedRole(currentUserRole) ? "CCL" : "All");
  const [statusList, setStatusList] = useState([]);
  const [fromDate, setFromDate] = useState(savedFromDate ? dayjs(savedFromDate) : null);
  const [toDate, setToDate] = useState(savedToDate ? dayjs(savedToDate) : null);
  const [dateErrors, setDateErrors] = useState({ from: "", to: "" });
  const [isToDateDisabled, setIsToDateDisabled] = useState(!fromDate);

  const [searchParams, setSearchParams] = useState({
    status: savedStatus || "",
    fromDate: savedFromDate || "",
    toDate: savedToDate || "",
  });
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isExportHidden, setIsExportHidden] = useState(false);

  useEffect(() => {
    const hidden = hideExportExcel(currentUserRole, roles);
    setIsExportHidden(hidden);
  }, [currentUserRole]);

  // useEffect for callViewPickListApi() as it's run on mount only
  useEffect(() => {
    callViewPickListApi();
  }, []);

  // useEffect for sessionStorage updates for related states
  useEffect(() => {
    sessionStorage.setItem("auditReport.fromDate", fromDate ? fromDate.format("YYYY-MM-DD") : "");
    sessionStorage.setItem("auditReport.toDate", toDate ? toDate.format("YYYY-MM-DD") : "");
    sessionStorage.setItem("auditReport.selectedpagenumber", activepage);
  }, [fromDate, toDate, activepage]);

  // View PickList API Call
  async function callViewPickListApi() {
    try {
      const response = await getViewPickList();
      if (response) {
        setStatusList(response);
        if (!status && response[0]?.lov_name) {
          setStatus(response[0].lov_name);
          setSearchParams({ ...searchParams, status: response[0].lov_name });
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  function handleDateChange(date, field) {
    if (field === "from") {
      setFromDate(date);
      setDateErrors((prev) => ({ ...prev, from: "" }));
      if (!date) {
        setToDate(null);
        setIsToDateDisabled(true);
        sessionStorage.removeItem("auditReport.fromDate");
        sessionStorage.removeItem("auditReport.toDate");
        return;
      }
      setIsToDateDisabled(false);
      sessionStorage.setItem("auditReport.fromDate", date.format("YYYY-MM-DD"));
    } else if (field === "to") {
      setToDate(date);
      setDateErrors((prev) => ({ ...prev, to: "" }));
      if (!date) {
        sessionStorage.removeItem("auditReport.toDate");
        return;
      }
      sessionStorage.setItem("auditReport.toDate", date.format("YYYY-MM-DD"));
    }
  }

  // function to show loading text
  function exportLoadingText() {
    return loadingExcel ? commonStrings.Exporting : commonStrings.ExportToExcel;
  }

  // set loading for export excel
  function loadingExcelVal(val) {
    setLoadingExcel(val);
  }

  // on click of export to excel
  const exportToExcel = () => {
    setLoadingExcel(true);
    if (exportExcelRef.current) {
      exportExcelRef.current.exportExcel();
    }
  };

  const handleSearchClick = () => {
    let errors = { from: "", to: "" };

    if (!fromDate && !toDate) {
      // both empty - send null dates, clear errors
      setSearchParams({
        status,
        fromDate: null,
        toDate: null,
      });
      setDateErrors(errors);
      setSearchTrigger(Math.random());
      return;
    }

    // If only one date entered show error
    if (fromDate && !toDate) {
      errors.to = "Please enter To Date";
    }
    if (!fromDate && toDate) {
      errors.from = "Please enter From Date";
    }
    setDateErrors(errors);

    if (errors.from || errors.to) {
      // validation errors, do not search
      return;
    }
    setSearchParams({
      status,
      fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
      toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
    });
    setSearchTrigger(Math.random());
    setActivepage(1);
  };

  // Status Change handler
  const handleStatusChange = (event) => {
    const value = event.target.value;
    setStatus(value);
    setActivepage(1);
    setFromDate(null);
    setToDate(null)
    setSearchParams({
      status: value,
      fromDate: null,
      toDate: null
    });

    sessionStorage.setItem("Auditreportfilter", value);
  };

  const handleCompanyChange = (event) => {
    setCompany(event.target.value);
    sessionStorage.setItem("auditReportCompany", event.target.value);
    setActivepage(1);
  };

  const statusOptions = useMemo(
    () =>
      statusList.map((item) => ({
        value: item.lov_name,
        label: item.lov_name,
      })),
    [statusList]
  );

  const companyOptions = useMemo(
    () => getCompanyFilterList(currentUserRole),
    [currentUserRole]
  );

  return (
    <Container className={globalClasses.sampleroot}>
      {showSnack.open && (
        <Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: "", severity: "" })} />
      )}
      <Grid container spacing={1} className={globalClasses.margin10}>
        <Grid item xs={4} sm={2} md={1}>
          <FilterSelect
            id="company-picklist"
            label={commonStrings.company}
            value={company}
            onChange={handleCompanyChange}
            options={companyOptions}
            disabled={isCompanyRestrictedRole(currentUserRole)}
          />
        </Grid>
        <Grid spacing={1} item xs={12} sm={4} md={3}>
          <Tooltip title={status || ""} placement="top" arrow>
            <div>
              <FilterSelect
                id="auditreport-picklist"
                label={commonStrings.pickList}
                value={status}
                onChange={handleStatusChange}
                options={statusOptions}
              />
            </div>
          </Tooltip>
        </Grid>
        <Grid spacing={1} item xs={6} sm={3} md={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={dateformat}
              label={auditReportString.fromDate}
              value={fromDate}
              onChange={(date) => handleDateChange(date, "from")}
              slotProps={{
                textField: {
                  name: "fromDate",
                  id: "fromDate",
                  error: Boolean(dateErrors.from),
                  helperText: dateErrors.from,
                  fullWidth: true,
                },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid spacing={1} item xs={6} sm={3} md={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={dateformat}
              label={auditReportString.toDate}
              value={toDate}
              minDate={fromDate}
              onChange={(date) => handleDateChange(date, "to")}
              disabled={isToDateDisabled}
              slotProps={{
                textField: {
                  name: "toDate",
                  id: "toDate",
                  error: Boolean(dateErrors.to),
                  helperText: dateErrors.to,
                  fullWidth: true,
                },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid spacing={1} item xs={4} sm={4} md={1}>
          <RoundButton
            onClick={handleSearchClick} 
            label="Search"
          />
        </Grid>
        <Grid spacing={1} item xs={8} sm={8} md={3}
          className={globalClasses.dfacjeg}
        >
          {isExportHidden && (
            totalRecords === 0 ? (
              <Tooltip title={commonStrings.noDataToExport} placement="top" arrow>
                <span>
                  <Fab 
                    onClick={exportToExcel} 
                    label={""} 
                    disabled 
                    iconAddExcel="excel"
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={commonStrings.exportToExcel} placement="top" arrow>
                <span>
                  <Fab 
                    onClick={exportToExcel} 
                    label={""} 
                    disabled={loadingExcel}
                    iconAddExcel="excel"
                  />
                </span>
              </Tooltip>
            )
          )}
        </Grid>
      </Grid>
      <AuditReportList
        loadingExcelVal={loadingExcelVal}
        exportRef={exportExcelRef}
        auditReportStatus={searchParams.status}
        fromDate={searchParams.fromDate}
        toDate={searchParams.toDate}
        activepage={activepage}
        setActivepage={setActivepage}
        totalrecord={totalRecords}
        updateTotalRecords={setTotalRecords}
        searchTrigger={searchTrigger}
        company={company}
      />
    </Container>
  );
};

export default AuditReport;
